import { type ComponentProps, type ReactElement, useState } from "react";
import { Keyboard } from "react-native";

import { Box, Pressable } from "../../../../mads";
import { FONT_FAMILY } from "../../../../mads/constants";
import { Text } from "../../../../mads/Text";
import { useThemedStylesheet } from "../../../../mads/ThemeProvider";
import { createThemedStylesheet } from "../../../../shared/util/createThemedStylesheet";
import { isWeb } from "../../../../shared/util/isWeb";
import type { TranslatedString } from "../../../I18n";

export interface PseudoInputProps {
  /** Accessible label for screen readers */
  accessibilityLabel?: TranslatedString;
  /** Optional icon after the text */
  contentAfter?: ReactElement;
  /** Whether the field appears disabled */
  disabled?: boolean;
  /** Whether the field appears invalid */
  invalid?: boolean;
  /** Whether to show the focus ring (e.g., when popup is open) */
  isActive?: boolean;
  /** Ref to the pressable trigger for programmatic focus. */
  focusRef?: ComponentProps<typeof Pressable>["ref"];
  /** Called when the button is pressed */
  onPress: () => void;
  /** Placeholder text when value is empty */
  placeholderText?: TranslatedString;
  /** Ref to access imperative methods like focus */
  ref?: ComponentProps<typeof Box>["ref"];
  /** Test ID */
  testID?: string;
  /** The displayed value text */
  value: string;
}

/**
 * A button that visually mimics a TextInput field.
 * Use as a combobox trigger that looks like a text input but opens a popup when pressed.
 */
export const PseudoInput = ({
  accessibilityLabel,
  contentAfter,
  disabled,
  invalid,
  isActive,
  focusRef,
  onPress,
  placeholderText,
  ref,
  testID,
  value,
}: PseudoInputProps) => {
  const styles = useThemedStylesheet(stylesheet);
  const [isFocused, setIsFocused] = useState(false);

  const displayText = value || placeholderText || "";
  const isPlaceholder = !value && !!placeholderText;
  const showFocusRing = isActive || isFocused;

  const wrapperStyles = [
    styles.wrapper,
    showFocusRing && styles.wrapperActive,
    invalid && styles.wrapperInvalid,
    invalid && showFocusRing && styles.wrapperInvalidActive,
    disabled && styles.wrapperDisabled,
  ];

  return (
    <Box sx={wrapperStyles} ref={ref}>
      <Pressable
        ref={focusRef}
        accessible
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={isWeb ? undefined : "button"}
        accessibilityState={{ disabled }}
        disabled={disabled}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onPressIn={Keyboard.dismiss}
        onPress={onPress}
        sx={styles.pressable}
        testID={testID}
      >
        <Box sx={styles.content}>
          <Text sx={[styles.text, isPlaceholder && styles.placeholder]}>{displayText}</Text>
          {contentAfter && <Box sx={styles.iconAfter}>{contentAfter}</Box>}
        </Box>
      </Pressable>
    </Box>
  );
};

const stylesheet = createThemedStylesheet((theme) => ({
  wrapper: {
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    padding: 3,
    borderColor: theme.colors.borderAction,
    overflow: "hidden",
    width: "100%",
  },
  wrapperActive: {
    borderColor: theme.colors.utilityFocus,
    borderWidth: 3,
    padding: 1,
  },
  wrapperInvalid: {
    borderColor: theme.colors.borderStatusCriticalStrong,
  },
  wrapperInvalidActive: {
    borderColor: theme.colors.borderStatusCriticalStrong,
    borderWidth: 3,
    padding: 1,
  },
  wrapperDisabled: {
    opacity: 0.4,
  },
  pressable: {
    borderRadius: theme.borderRadius.sm,
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    borderRadius: theme.borderRadius.sm,
  },
  text: {
    flex: 1,
    paddingVertical: theme.spacing.s3,
    paddingHorizontal: theme.spacing.s3,
    fontSize: theme.fontSizes.f3,
    fontFamily: FONT_FAMILY,
    color: theme.colors.foregroundStrong,
  },
  placeholder: {
    color: theme.colors.foregroundWeak,
  },
  iconAfter: {
    paddingLeft: theme.spacing.s1,
    paddingRight: theme.spacing.s2,
  },
}));
